import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Download, LogIn, AtSign, Check, Coffee, Sparkles, Camera, Settings, AlertTriangle, BarChart3, HeartCrack, Globe, Lock, Copy } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { getAccountSummary, getAccountExport, setHandle, setProfilePublic, getSubscription, openBillingPortal, getCreatorId, getAdminCancellations, getAdminBusiness, switchToYearly, getFollowList } from "../lib/api";
import CancelModal from "../components/CancelModal";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
}

export default function Account() {
  const { user, loading: authLoading, refresh } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [subscription, setSubscription] = useState({ state: "none" });
  const [loading, setLoading] = useState(true);
  const [handleInput, setHandleInput] = useState("");
  const [savingHandle, setSavingHandle] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    (async () => {
      try {
        const [s, sub] = await Promise.all([
          getAccountSummary(),
          getSubscription(getCreatorId()).catch(() => ({ state: "none" })),
        ]);
        setSummary(s);
        setSubscription(sub || { state: "none" });
        setHandleInput(s.user.handle || "");
      } finally { setLoading(false); }
    })();
  }, [user, authLoading]);

  const submitHandle = async (e) => {
    e.preventDefault();
    const h = handleInput.trim().toLowerCase();
    if (!h) return;
    setSavingHandle(true);
    try {
      await setHandle(h);
      await refresh();
      const s = await getAccountSummary();
      setSummary(s);
      toast.success("Handle saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save handle");
    } finally {
      setSavingHandle(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const data = await getAccountExport();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `otaku-index-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const manage = async () => {
    setOpeningPortal(true);
    try {
      const { portal_url } = await openBillingPortal();
      window.location.href = portal_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open the billing portal");
      setOpeningPortal(false);
    }
  };

  if (authLoading || (user && loading)) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" /></div>;
  }

  if (!user) {
    return (
      <div className="mx-auto max-w-[900px] px-6 py-24 md:px-10 text-center">
        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 10</p>
        <h1 className="mt-2 font-display text-6xl uppercase text-white md:text-7xl">Account</h1>
        <p className="mt-4 text-neutral-400">Sign in to see your account details, tips, unlocks, and export your data.</p>
        <button
          data-testid="account-signin-btn"
          onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="mt-6 inline-flex items-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
        >
          <LogIn className="h-4 w-4" /> Sign in with Google
        </button>
      </div>
    );
  }

  const dollars = ((summary?.tips_total_cents || 0) / 100).toFixed(2);

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-10 md:px-10">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 10</p>
      <h1 className="mt-2 font-display text-6xl uppercase text-white md:text-8xl">Account</h1>

      {/* Identity */}
      <section className="mt-10 grid grid-cols-1 gap-6 border-y border-white/10 py-8 md:grid-cols-[auto_1fr]">
        <div className="flex items-start gap-4">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-24 w-24 border border-white/20 object-cover" />
          ) : (
            <div className="flex h-24 w-24 items-center justify-center border border-white/20 bg-neutral-900 font-display text-4xl text-white">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Signed in as</p>
          <h2 className="mt-1 font-display text-4xl uppercase leading-tight text-white md:text-5xl">{user.name || user.email}</h2>
          <p className="mt-1 truncate text-sm text-neutral-400">{user.email}</p>
          {user.handle && (
            <p className="mt-2 flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-[#FF3B30]">
              <AtSign className="h-3 w-3" /> {user.handle}
            </p>
          )}
          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Joined {fmt(user.created_at)}
          </p>
        </div>
      </section>

      {/* Handle setter */}
      <section className="mt-10">
        <div className="flex items-center gap-3">
          <AtSign className="h-4 w-4 text-[#FF3B30]" />
          <h2 className="font-display text-3xl uppercase tracking-widest text-white">Public Handle</h2>
        </div>
        <p className="mt-2 max-w-lg text-sm text-neutral-500">
          Shown on your shared persona pages instead of the raw share ID. Lowercase letters, digits, and underscores. 3–24 chars.
        </p>
        <form onSubmit={submitHandle} className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex flex-1 min-w-[200px] items-center border border-white/15 bg-black px-3">
            <AtSign className="h-4 w-4 text-neutral-500" />
            <input
              data-testid="handle-input"
              value={handleInput}
              onChange={(e) => setHandleInput(e.target.value)}
              placeholder="e.g. shonen_daddy"
              maxLength={24}
              className="w-full bg-transparent px-2 py-3 text-white placeholder:text-neutral-600 focus:outline-none"
            />
          </div>
          <button
            data-testid="handle-save-btn"
            type="submit"
            disabled={savingHandle}
            className="flex items-center gap-2 border border-white/15 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-[#D62828] disabled:opacity-50"
          >
            {savingHandle ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            Save
          </button>
        </form>
      </section>

      {/* Public profile visibility */}
      <PublicProfileSection
        user={user}
        onChange={async () => {
          await refresh();
          const s = await getAccountSummary();
          setSummary(s);
        }}
      />

      {/* Pro Pass subscription */}
      <ProPassCard
        subscription={subscription}
        onManage={manage}
        openingPortal={openingPortal}
        onCancel={() => setCancelOpen(true)}
      />

      {/* Yearly upgrade nudge — shown for active monthly subscribers only */}
      <YearlyUpgradeBanner
        subscription={subscription}
        onSwitched={async () => {
          const s = await getSubscription(getCreatorId()).catch(() => ({ state: "none" }));
          setSubscription(s || { state: "none" });
        }}
      />

      <CancelModal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        saveOfferAvailable={!subscription?.save_offer_used}
        onDone={async () => {
          const s = await getSubscription(getCreatorId()).catch(() => ({ state: "none" }));
          setSubscription(s || { state: "none" });
        }}
      />

      {/* Admin: cancellation analytics — only visible when the signed-in user's email is in ADMIN_EMAILS */}
      {user?.is_admin && <BusinessDashboard />}
      {user?.is_admin && <CancellationAnalytics />}

      {/* Stats */}
      <section className="mt-12 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-4">
        <Stat icon={<Coffee className="h-4 w-4 text-[#FF3B30]" />} label="Tips sent" value={summary.tips_count} sub={`$${dollars} total`} accent />
        <Stat icon={<Sparkles className="h-4 w-4" />} label="Unlocks" value={summary.unlocks.length} sub={summary.unlocks.join(", ") || "—"} />
        <Stat icon={<Camera className="h-4 w-4" />} label="Snapshots" value={summary.snapshot_count} />
        <Stat icon={<AtSign className="h-4 w-4" />} label="User ID" value={<span className="font-mono text-sm">{user.user_id}</span>} />
      </section>

      {/* Following */}
      <FollowingSection />

      {/* Export */}
      <section className="mt-12 border border-white/15 bg-neutral-950 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-3xl uppercase tracking-widest text-white">Export Your Data</h2>
            <p className="mt-2 max-w-lg text-sm text-neutral-400">
              One JSON file with your persona snapshots, unlocks, payments, and the current catalogue. Handy for backups or switching tools.
            </p>
          </div>
          <button
            data-testid="export-btn"
            onClick={doExport}
            disabled={exporting}
            className="flex flex-shrink-0 items-center gap-2 border border-white/15 bg-white px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-black hover:bg-[#FF3B30] hover:text-white disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Download JSON
          </button>
        </div>
      </section>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/personality" className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black">
          View Persona
        </Link>
        <Link to="/premium" className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-white hover:text-black">
          Send another tip
        </Link>
      </div>
    </div>
  );
}

function Stat({ icon, label, value, sub, accent }) {
  return (
    <div className={`bg-neutral-950 p-5 ${accent ? "border-l-2 border-[#FF3B30]" : ""}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        {icon}
        {label}
      </div>
      <p className="mt-2 font-display text-4xl leading-none text-white md:text-5xl">{value}</p>
      {sub && <p className="mt-2 truncate font-mono text-[10px] uppercase tracking-widest text-neutral-500">{sub}</p>}
    </div>
  );
}

function ProPassCard({ subscription, onManage, openingPortal, onCancel }) {
  const state = subscription?.state || "none";
  const expiresAt = subscription?.expires_at;
  const humanExpiry = fmt(expiresAt);

  const config = {
    trialing:  { title: "Free trial", body: `You're on your 7-day free trial — Stripe will charge for the first period on ${humanExpiry}. Cancel from the billing portal any time before that and it's fully free.`, tone: "emerald" },
    active:    { title: "Active", body: `Renews on ${humanExpiry}. Manage payment or cancel below — cancellation takes effect at the end of your paid period.`, tone: "emerald" },
    canceling: { title: "Ending soon", body: `You've cancelled auto-renew. You'll keep Pro Pass access until ${humanExpiry}, then it stops. Resume anytime.`, tone: "amber" },
    past_due:  { title: "Payment failed", body: "Stripe couldn't charge your card for this period. Update your payment method to keep Pro Pass active — otherwise your pass ends soon.", tone: "amber" },
    gift:      { title: "Gifted month", body: `Someone gave you 30 days of Pro Pass — thank them! Access ends ${humanExpiry} and won't auto-renew. Subscribe from Premium if you want to keep going.`, tone: "emerald" },
    lifetime:  { title: "Lifetime access", body: "You bought Pro Pass back when it was a one-time unlock — that's yours forever. No renewal needed.", tone: "emerald" },
    expired:   { title: "Not active", body: "Your Pro Pass has ended. Restart from Premium anytime — your catalogue and settings are exactly where you left them.", tone: "neutral" },
    none:      { title: "Not subscribed", body: "You're using Otaku//Index free, which is 90% of the app. Pro Pass adds Persona re-runs, History, Discover, Schedule, and Bulk Import — $5/mo or $39/yr, 7-day free trial, cancel anytime. Or grab the Persona Only tier for $2/mo.", tone: "neutral" },
  };
  const c = config[state] || config.none;
  const showManage = ["active", "canceling", "past_due", "trialing"].includes(state);

  const borderClass = c.tone === "emerald" ? "border-emerald-500/40" : c.tone === "amber" ? "border-amber-500/40" : "border-white/15";

  return (
    <section className="mt-12" data-testid="account-pro-pass">
      <div className="flex items-center gap-3">
        <Sparkles className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">Pro Pass</h2>
      </div>
      <div className={`mt-4 flex flex-col gap-4 border ${borderClass} bg-neutral-950 p-6 md:flex-row md:items-center md:justify-between`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-400">
            {c.tone === "amber" && <AlertTriangle className="h-3 w-3 text-amber-400" />}
            Status
          </div>
          <p data-testid="pro-pass-state" className="mt-1 font-display text-3xl uppercase text-white md:text-4xl">{c.title}</p>
          <p className="mt-2 max-w-xl text-sm text-neutral-300">{c.body}</p>
        </div>
        <div className="flex flex-col gap-2 md:flex-shrink-0">
          {showManage ? (
            <>
              <button
                data-testid="account-manage-subscription-btn"
                onClick={onManage}
                disabled={openingPortal}
                className="flex items-center justify-center gap-2 border border-white/20 bg-white px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-black hover:bg-[#FF3B30] hover:text-white disabled:opacity-50"
              >
                {openingPortal ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
                Manage subscription
              </button>
              {state !== "canceling" && (
                <button
                  data-testid="account-cancel-subscription-btn"
                  onClick={onCancel}
                  className="flex items-center justify-center gap-2 border border-white/15 bg-transparent px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-400 hover:border-neutral-400 hover:text-white"
                >
                  <HeartCrack className="h-3 w-3" />
                  Cancel Pro Pass
                </button>
              )}
            </>
          ) : (
            <Link
              to="/premium"
              data-testid="account-goto-support-btn"
              className="flex items-center justify-center gap-2 border border-white/20 bg-[#FF3B30] px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              <Sparkles className="h-4 w-4" />
              {state === "expired" || state === "gift" ? "Resubscribe" : "See Pro Pass"}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

function humanizeKey(k) {
  return (k || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Owner-only Business Dashboard — reads /api/admin/business. Aggregates only;
// no individual user data leaves the server. Designed to be scannable in a
// glance, so it's just a header row + 4 headline stats + 2 window stats.
// ---------------------------------------------------------------------------
const WINDOW_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1y", days: 365 },
];
function FollowingSection() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getFollowList()
      .then((d) => { if (!cancelled) setItems(d.following || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <section data-testid="account-following" className="mt-12 border border-white/15 bg-neutral-950 p-6">
      <div className="flex items-baseline justify-between border-b border-white/10 pb-3">
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">Following</h2>
        <Link
          to="/feed"
          data-testid="account-view-feed"
          className="font-mono text-[10px] uppercase tracking-widest text-neutral-400 hover:text-white"
        >
          View Feed →
        </Link>
      </div>
      {loading ? (
        <div className="mt-6 flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-[#FF3B30]" />
        </div>
      ) : items && items.length > 0 ? (
        <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((f) => (
            <li key={f.handle} data-testid={`following-${f.handle}`} className="flex items-center gap-3 border border-white/10 bg-black/40 p-3">
              {f.picture ? (
                <img src={f.picture} alt={f.display_name} crossOrigin="anonymous"
                  className="h-10 w-10 border border-white/15 object-cover" />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center border border-white/15 bg-[#111] font-display uppercase text-white">
                  {(f.display_name || "?").charAt(0)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-sm uppercase tracking-tight text-white">{f.display_name}</p>
                <p className="truncate font-mono text-[10px] uppercase tracking-widest text-neutral-500">@{f.handle}</p>
              </div>
              <Link
                to={`/u/${f.handle}`}
                className="border border-white/20 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
              >
                View
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-sm text-neutral-400">
          You&apos;re not following anyone yet. Open any public profile at <span className="font-mono text-white">/u/handle</span> and tap Follow.
        </p>
      )}
    </section>
  );
}



function BusinessDashboard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getAdminBusiness(days)
      .then(setData)
      .catch((err) => setError(err?.response?.data?.detail || "Couldn't load business snapshot"))
      .finally(() => setLoading(false));
  }, [days]);

  const t = data?.totals;
  const subs = data?.subscriptions;
  const rev = data?.revenue;
  const eng = data?.engagement;
  const w = data?.window_counts;
  const trends = data?.trends;

  return (
    <section className="mt-12" data-testid="admin-business">
      <div className="flex flex-wrap items-center gap-3">
        <BarChart3 className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">Business</h2>
        <span className="border border-white/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-neutral-400">Owner only</span>
        <div className="ml-auto flex items-center gap-1 border border-white/15" role="tablist" data-testid="admin-business-range">
          {WINDOW_PRESETS.map((p) => (
            <button
              key={p.days}
              data-testid={`admin-business-range-${p.days}`}
              onClick={() => setDays(p.days)}
              className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest ${days === p.days ? "bg-[#FF3B30] text-white" : "text-neutral-400 hover:text-white"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-2 max-w-lg text-sm text-neutral-500">
        Aggregate metrics only — no individual user details are exposed. Revenue figures come from Stripe & the payment ledger.
      </p>

      {loading && (
        <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}
      {error && !loading && (
        <p className="mt-6 border border-red-500/30 bg-red-500/5 p-3 font-mono text-[10px] uppercase tracking-widest text-red-400">
          {error}
        </p>
      )}

      {data && !loading && (
        <>
          {/* Revenue — top of the fold because it's the most-consulted set */}
          <SectionHeader label="Revenue" testid="admin-business-revenue" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard label="MRR"           value={fmtDollars(rev.mrr_cents)}   accent testid="metric-mrr" />
            <MetricCard label="ARR estimate"  value={fmtDollars(rev.arr_cents)}         testid="metric-arr" />
            <MetricCard label="Tips (all)"    value={fmtDollars(rev.tips_total_cents)}  testid="metric-tips-total" />
            <MetricCard label={`Tips · last ${days}d`} value={fmtDollars(rev.tips_window_cents)} testid="metric-tips-window" />
          </div>

          {/* Users & conversion */}
          <SectionHeader label="Users" testid="admin-business-users" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="admin-business-totals">
            <MetricCard label="Total users"      value={t.users}                    testid="metric-total-users" />
            <MetricCard label="Active Premium"   value={t.active_premium} accent    testid="metric-active-premium" />
            <MetricCard label="Free users"       value={t.free}                     testid="metric-free-users" />
            <MetricCard label="Conversion"       value={`${t.conversion_pct}%`}     testid="metric-conversion" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard label="Public profiles"  value={t.public_profiles}          testid="metric-public-profiles" />
            <MetricCard label="Handles claimed"  value={t.handles_set}              testid="metric-handles-set" />
            <MetricCard label="Trialing"         value={subs.trialing}              testid="metric-trialing" />
            <MetricCard label="Canceling"        value={subs.canceling}             testid="metric-canceling" />
          </div>

          {/* Plan mix */}
          <SectionHeader label="Plan mix" testid="admin-business-plans" />
          <PlanMix mix={subs.plan_mix || {}} />

          {/* Engagement */}
          <SectionHeader label="Engagement" testid="admin-business-engagement" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard label="Anime tracked"        value={eng.total_catalogue_rows}    testid="metric-catalogue" />
            <MetricCard label="Avg / user"           value={eng.avg_catalogue_per_user}  testid="metric-avg-catalogue" />
            <MetricCard label="Persona snapshots"    value={eng.persona_snapshots}       testid="metric-persona-snapshots" />
            <MetricCard label="Badges earned"        value={eng.badges_earned}           testid="metric-badges" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
            <MetricCard label="Follow relationships" value={eng.total_follows}           testid="metric-follows" />
            <MetricCard label="Avg follows/user"     value={eng.avg_follows_per_user}    testid="metric-avg-follows" />
            <MetricCard label="Open feature reqs"    value={eng.open_feature_requests}   testid="metric-open-features" />
            <MetricCard label="Past due"             value={subs.past_due}               testid="metric-past-due" />
          </div>

          {/* Window activity */}
          <SectionHeader label={`Last ${days} days`} testid="admin-business-window" />
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <MetricCard label="Sign-ups"            value={w.new_signups}         accent testid="metric-new-signups" />
            <MetricCard label="Cancellations"       value={w.cancellations}              testid="metric-cancellations" />
            <MetricCard label="Anime added"         value={w.new_catalogue_rows}         testid="metric-new-catalogue" />
            <MetricCard label="Persona shares"      value={w.new_persona_shares}         testid="metric-new-persona" />
            <MetricCard label="New follows"         value={w.new_follows}                testid="metric-new-follows" />
          </div>

          {/* Sparkline trends */}
          <SectionHeader label="Trend" testid="admin-business-trend" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TrendChart label="Sign-ups per day" testid="trend-signups" data={trends?.signups || []} color="#FF3B30" />
            <TrendChart label="Cancellations per day" testid="trend-cancellations" data={trends?.cancellations || []} color="#94a3b8" />
          </div>
        </>
      )}
    </section>
  );
}

function SectionHeader({ label, testid }) {
  return (
    <p
      data-testid={testid}
      className="mt-8 mb-3 border-b border-white/10 pb-2 font-mono text-[10px] uppercase tracking-[0.4em] text-neutral-400"
    >
      // {label}
    </p>
  );
}

function fmtDollars(cents) {
  const dollars = (cents || 0) / 100;
  return `$${dollars.toFixed(2)}`;
}

const PLAN_LABELS = {
  pro_pass_monthly: "Pro Pass · Monthly",
  pro_pass_yearly: "Pro Pass · Yearly",
  persona_only_monthly: "Persona Only",
  pro_pass_gift_month: "Gift Month",
};

function PlanMix({ mix }) {
  const entries = Object.entries(mix);
  if (entries.length === 0) {
    return (
      <p data-testid="plan-mix-empty" className="font-mono text-[11px] uppercase tracking-widest text-neutral-500">
        No active subscriptions yet.
      </p>
    );
  }
  const total = entries.reduce((sum, [, v]) => sum + v, 0);
  return (
    <div data-testid="plan-mix" className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {entries.map(([key, count]) => {
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={key} data-testid={`plan-mix-${key}`} className="border border-white/15 bg-neutral-950 p-4">
            <div className="flex items-baseline justify-between">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                {PLAN_LABELS[key] || key}
              </p>
              <p className="font-mono text-[11px] text-white">{count} · {pct}%</p>
            </div>
            <div className="mt-3 h-1.5 border border-white/10 bg-black">
              <div className="h-full bg-[#FF3B30]" style={{ width: `${Math.max(3, pct)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrendChart({ label, data, color, testid }) {
  const max = Math.max(1, ...data.map((d) => d.count || 0));
  return (
    <div data-testid={testid} className="border border-white/15 bg-neutral-950 p-4">
      <p className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-widest text-neutral-500">
        <span>{label}</span>
        <span className="text-white">peak {max}</span>
      </p>
      <div className="mt-4 flex h-16 items-end gap-[2px]">
        {data.length === 0 ? (
          <p className="w-full text-center font-mono text-[10px] uppercase tracking-widest text-neutral-600">
            No data
          </p>
        ) : data.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.count}`}
            className="flex-1"
            style={{ height: `${Math.max(2, (d.count / max) * 100)}%`, backgroundColor: color }}
          />
        ))}
      </div>
      <p className="mt-2 flex justify-between font-mono text-[9px] uppercase tracking-widest text-neutral-600">
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </p>
    </div>
  );
}

function MetricCard({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border p-4 ${accent ? "border-[#FF3B30] bg-[#FF3B30]/8" : "border-white/15 bg-neutral-950"}`}
    >
      <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className="mt-2 font-display text-4xl uppercase leading-none tracking-tight text-white">{value ?? 0}</p>
    </div>
  );
}

function CancellationAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getAdminCancellations()
      .then(setData)
      .catch((err) => setError(err?.response?.data?.detail || "Couldn't load cancellation data"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="mt-12" data-testid="admin-cancellations">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-4 w-4 text-[#FF3B30]" />
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">Why people leave</h2>
        <span className="border border-white/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-neutral-400">Admin</span>
      </div>
      <p className="mt-2 max-w-lg text-sm text-neutral-500">
        Aggregated cancellation feedback from Stripe. Only you can see this.
      </p>

      {loading && (
        <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading cancellations...
        </div>
      )}

      {error && !loading && (
        <p className="mt-6 border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-300">{error}</p>
      )}

      {data && !loading && (
        data.total === 0 && (data.save_offer?.shown || 0) === 0 ? (
          <p className="mt-6 border border-white/10 bg-neutral-950 p-6 text-sm text-neutral-400">
            No cancellations yet. When someone cancels through the Stripe portal, their reason lands here.
          </p>
        ) : (
          <div className="mt-6 space-y-6">
            {/* Save-offer performance */}
            {data.save_offer && data.save_offer.shown > 0 && (
              <SaveOfferStats stats={data.save_offer} />
            )}
            {data.total > 0 && (
              <>
                <p data-testid="admin-cancellations-total" className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  {data.total} cancellation{data.total === 1 ? "" : "s"} total
                </p>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <ReasonChart title="Top feedback" data={data.feedback_counts} testid="feedback-counts" total={data.total} />
                  <ReasonChart title="Reasons given" data={data.reason_counts} testid="reason-counts" total={data.total} />
                </div>
              </>
            )}
            {data.recent?.length > 0 && (
              <div className="border border-white/10 bg-neutral-950">
                <div className="border-b border-white/10 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                  Recent (up to 20)
                </div>
                <ul className="divide-y divide-white/5">
                  {data.recent.map((r) => (
                    <li key={r.subscription_id} className="flex flex-col gap-1 px-4 py-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                          {r.canceled_at ? new Date(r.canceled_at).toLocaleDateString() : "—"}
                        </p>
                        <p className="mt-1 text-sm text-white">
                          {humanizeKey(r.feedback) || humanizeKey(r.reason) || "No reason given"}
                        </p>
                        {r.comment && <p className="mt-1 text-xs text-neutral-400">&ldquo;{r.comment}&rdquo;</p>}
                      </div>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-600">{r.subscription_id.slice(0, 14)}...</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )
      )}
    </section>
  );
}

function ReasonChart({ title, data, testid, total }) {
  const entries = Object.entries(data || {});
  if (entries.length === 0) {
    return (
      <div className="border border-white/10 bg-neutral-950 p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{title}</p>
        <p className="mt-3 text-sm text-neutral-500">No data yet.</p>
      </div>
    );
  }
  const max = Math.max(...entries.map(([, v]) => v));
  return (
    <div data-testid={testid} className="border border-white/10 bg-neutral-950 p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{title}</p>
      <ul className="mt-3 space-y-2">
        {entries.map(([k, v]) => (
          <li key={k} className="text-sm">
            <div className="flex items-center justify-between text-neutral-200">
              <span>{humanizeKey(k)}</span>
              <span className="font-mono text-[10px] text-neutral-500">{v} · {Math.round((v / total) * 100)}%</span>
            </div>
            <div className="mt-1 h-1.5 w-full bg-white/5">
              <div className="h-full bg-[#FF3B30]" style={{ width: `${(v / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function SaveOfferStats({ stats }) {
  const { shown, saved, declined, save_rate } = stats;
  return (
    <div
      data-testid="save-offer-stats"
      className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-5"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">50% Save Offer · Intercept</p>
          <p className="mt-2 flex items-baseline gap-2">
            <span data-testid="save-offer-rate" className="font-display text-5xl leading-none text-white">{save_rate}%</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">save rate</span>
          </p>
          <p className="mt-2 text-xs text-neutral-500">
            {saved} of {shown} people who saw the offer accepted the 50% off. {declined} declined and went on to cancel.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <SaveOfferPill testid="save-offer-shown" label="Shown" value={shown} tone="neutral" />
          <SaveOfferPill testid="save-offer-saved" label="Saved" value={saved} tone="green" />
          <SaveOfferPill testid="save-offer-declined" label="Declined" value={declined} tone="red" />
        </div>
      </div>
    </div>
  );
}

function SaveOfferPill({ label, value, tone, testid }) {
  const toneClass = tone === "green"
    ? "border-emerald-500/50 text-emerald-300"
    : tone === "red"
      ? "border-neutral-500/40 text-neutral-300"
      : "border-white/15 text-white";
  return (
    <div data-testid={testid} className={`border ${toneClass} bg-black/30 px-3 py-2 text-center`}>
      <p className="font-display text-2xl leading-none">{value}</p>
      <p className="mt-1 font-mono text-[9px] uppercase tracking-widest opacity-70">{label}</p>
    </div>
  );
}


function YearlyUpgradeBanner({ subscription, onSwitched }) {
  const [switching, setSwitching] = useState(false);
  const state = subscription?.state;
  const lookup = subscription?.subscription?.lookup_key;
  // Only nudge active monthly subscribers. Trialing / canceling / past_due / yearly-already users get nothing.
  const eligible = state === "active" && lookup === "pro_pass_monthly";
  if (!eligible) return null;

  const doSwitch = async () => {
    if (!window.confirm("Switch to yearly? Stripe will prorate your current period against the $39 yearly charge — you'll be billed the difference now, then renew yearly.")) return;
    setSwitching(true);
    try {
      await switchToYearly();
      toast.success("Switched to yearly · saved you $21/year");
      onSwitched?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't switch to yearly");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <section className="mt-6" data-testid="yearly-upgrade-banner">
      <div className="flex flex-col gap-4 border border-emerald-500/40 bg-emerald-500/5 p-5 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">Save $21 / year</p>
          <p className="mt-1 text-base text-white">
            Switch to yearly and lock in Pro Pass for <span className="text-white">$39</span> instead of $60 — same card, same account, no re-onboarding.
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
            Your remaining monthly balance is prorated against the yearly total.
          </p>
        </div>
        <button
          data-testid="switch-to-yearly-btn"
          onClick={doSwitch}
          disabled={switching}
          className="flex flex-shrink-0 items-center justify-center gap-2 border border-white/20 bg-emerald-400 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-black hover:bg-white disabled:opacity-50"
        >
          {switching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Switch to yearly
        </button>
      </div>
    </section>
  );
}



// ---------------------------------------------------------------------------
// Public profile privacy toggle
// ---------------------------------------------------------------------------
function PublicProfileSection({ user, onChange }) {
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!user) return null;

  const publicOn = !!user.profile_public;
  const hasHandle = !!user.handle;
  const profileUrl = hasHandle ? `${window.location.origin}/u/${user.handle}` : null;

  const toggle = async () => {
    if (!hasHandle) {
      toast.error("Set a public handle first (above).");
      return;
    }
    setSaving(true);
    try {
      await setProfilePublic(!publicOn);
      await onChange();
      toast.success(publicOn ? "Profile is now private" : "Profile is now public");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update visibility");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    if (!profileUrl) return;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast.success("Profile link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — copy manually");
    }
  };

  return (
    <section className="mt-10" data-testid="profile-privacy-section">
      <div className="flex items-center gap-3">
        {publicOn ? (
          <Globe className="h-4 w-4 text-[#FF3B30]" />
        ) : (
          <Lock className="h-4 w-4 text-neutral-500" />
        )}
        <h2 className="font-display text-3xl uppercase tracking-widest text-white">
          Public Profile
        </h2>
      </div>
      <p className="mt-2 max-w-lg text-sm text-neutral-500">
        Off by default. When on, anyone with your handle can view a read-only
        version of your catalogue and stats at <span className="font-mono text-neutral-300">otakuindex.app/u/{user.handle || "your-handle"}</span>. Ratings, hours, and top genres are shown; email and account details are never exposed.
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          data-testid="profile-public-toggle"
          onClick={toggle}
          disabled={saving || !hasHandle}
          role="switch"
          aria-checked={publicOn}
          className={`flex items-center gap-2 border px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest transition-colors disabled:opacity-40 ${
            publicOn
              ? "border-[#FF3B30] bg-[#FF3B30] text-white hover:bg-[#D62828]"
              : "border-white/15 bg-transparent text-white hover:border-[#FF3B30]"
          }`}
        >
          {saving ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : publicOn ? (
            <Globe className="h-3 w-3" />
          ) : (
            <Lock className="h-3 w-3" />
          )}
          {publicOn ? "Profile is public" : "Make my profile public"}
        </button>

        {publicOn && profileUrl && (
          <>
            <a
              data-testid="profile-open-link"
              href={profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[11px] uppercase tracking-widest text-neutral-300 underline decoration-white/20 underline-offset-4 hover:text-white"
            >
              {profileUrl.replace(/^https?:\/\//, "")}
            </a>
            <button
              data-testid="profile-copy-link"
              onClick={copyLink}
              className="flex items-center gap-2 border border-white/15 bg-transparent px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-white hover:border-[#FF3B30]"
            >
              {copied ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copied" : "Copy link"}
            </button>
          </>
        )}
      </div>

      {!hasHandle && (
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-amber-400">
          Set a public handle above before enabling this.
        </p>
      )}
    </section>
  );
}
